/**
 * Parse ```yaml quiz``` fence from markdown body (client-side).
 */

import { parse as parseYaml } from 'yaml';

export interface QuizQuestion {
  id: string;
  domain: string;
  stem: string;
  options: Record<string, string>;
  answer: string;
  explanation?: string;
  difficulty?: string;
  stable_id?: string;
  status?: string;
  source_ref?: string;
  appendix_a?: string;
  tags?: string[];
}

export type QuizMode = 'exam' | 'survey';
export type QuizRole = 'bank' | 'snapshot' | 'exam';

export interface QuizConfig {
  domain_weights?: Record<string, number>;
  /** Minutes; omit / 0 / null = no timer (exam generator default is 90 when omitted). */
  time_limit_minutes?: number | null;
  randomize?: boolean;
  /** When true (default), permute a-d option order each attempt / flash round. */
  shuffle_options?: boolean;
  passing_score?: number;
  /** exam (default) = scored mock; survey = preference / would-you-rather. */
  mode?: QuizMode;
  /** bank = session builder pool; snapshot = frozen dress rehearsal. */
  role?: QuizRole;
  source_bank_slug?: string;
  exclude_mock_slug?: string;
  default_session?: {
    count?: number;
    randomize?: boolean;
    verified_only?: boolean;
  };
}

export function resolveQuizRole(quizConfig: QuizConfig): QuizRole {
  const role = String(quizConfig.role || '').toLowerCase();
  if (role === 'bank') return 'bank';
  if (role === 'snapshot') return 'snapshot';
  return 'exam';
}

export function resolveQuizMode(quizConfig: QuizConfig): QuizMode {
  return String(quizConfig.mode || '').toLowerCase() === 'survey' ? 'survey' : 'exam';
}

/** Resolve timed duration in minutes; 0 means untimed. */
export function resolveQuizTimeLimitMinutes(
  quizConfig: QuizConfig,
  mode: QuizMode = 'exam',
): number {
  if (quizConfig.time_limit_minutes === undefined) {
    return mode === 'survey' ? 0 : 90;
  }
  const raw = Number(quizConfig.time_limit_minutes);
  if (Number.isFinite(raw) && raw > 0) return raw;
  return 0;
}

const FENCE_RE = /```yaml(?:\s+quiz)?\s*\r?\n([\s\S]*?)\r?\n```/;

function normalizeOptions(options: unknown): Record<string, string> {
  if (!options || typeof options !== 'object' || Array.isArray(options)) return {};
  const out: Record<string, string> = {};
  const obj = options as Record<string, unknown>;
  for (const key of ['a', 'b', 'c', 'd']) {
    if (obj[key] != null) out[key] = String(obj[key]);
  }
  return out;
}

export function parseQuizFromContent(content: string): QuizQuestion[] {
  const m = content.match(FENCE_RE);
  if (!m) return [];
  try {
    const data = parseYaml(m[1]) as { questions?: unknown[] };
    const raw = Array.isArray(data?.questions) ? data.questions : [];
    return raw
      .filter((q): q is Record<string, unknown> => !!q && typeof q === 'object')
      .filter((q) => q.id && q.stem)
      .map((q) => {
        const out: QuizQuestion = {
          id: String(q.id),
          domain: q.domain ? String(q.domain) : 'general',
          stem: String(q.stem),
          options: normalizeOptions(q.options),
          answer: String(q.answer || 'a').toLowerCase(),
          explanation: q.explanation ? String(q.explanation) : undefined,
          difficulty: q.difficulty ? String(q.difficulty) : undefined,
        };
        if (q.stable_id != null) out.stable_id = String(q.stable_id);
        if (q.status != null) out.status = String(q.status);
        if (q.source_ref != null) out.source_ref = String(q.source_ref);
        if (q.appendix_a != null) out.appendix_a = String(q.appendix_a);
        if (Array.isArray(q.tags)) out.tags = q.tags.map(String);
        return out;
      });
  } catch {
    return [];
  }
}

export function shuffleQuestions<T>(items: T[]): T[] {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
